/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import React, { useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import i18n from "i18next";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useTranslation } from "react-i18next";
import { Field, formValueSelector, reduxForm } from "redux-form";

import FormField from "components/FormField";
import FormSelector from "components/FormSelector";
import FormTimeSelector from "components/FormTimeSelector";
import FormCheckbox from "components/FormCheckbox";
import FormSelectorMulti from "components/FormSelectorMulti";
import UploadImageInput from "components/UploadImageInput/UploadImageInput";

import { newAlertMessage } from "store/actions/globalAlertActions";
import { uploadProjectImage, deleteProjectImage } from "store/actions/mainUserActions";

import serviceUtils from "./serviceUtils";

const { isPositiveInteger, getMinutesFromHHMM, currencyOptions, getWeekDaysOptions } = serviceUtils;

const validate = (values) => {
  const errors = {};

  const required = [
    "name",
    "startTime",
    "endTime",
    "serviceDuration",
    "minPreviousTime",
    "minCancelTime",
  ];

  required.forEach((f) => {
    if (values[f] === undefined || values[f] === null || values[f] === "") {
      errors[f] = i18n.t("Required");
    }
  });

  // Available days
  if (!values.availableDays || values.availableDays.length === 0) {
    errors.availableDays = i18n.t("Required");
  }

  // Cost
  if (!isPositiveInteger(values.cost)) {
    errors.cost = i18n.t("costPositive");
  }

  // Resource limit
  if (values.resourcesLimited && !isPositiveInteger(values.resourcesLimitNumber)) {
    errors.resourcesLimitNumber = i18n.t("requiredPositiveInteger");
  }

  if (values.clientLimit && !isPositiveInteger(values.clientLimit)) {
    errors.clientLimit = i18n.t("requiredPositiveInteger");
  }

  const clientLimitNumber = Number(values.clientLimit);
  const resourcesLimitNumber = Number(values.resourcesLimitNumber);

  if (
    values.resourcesLimited &&
    isPositiveInteger(values.clientLimit) &&
    isPositiveInteger(values.resourcesLimitNumber) &&
    clientLimitNumber > resourcesLimitNumber
  ) {
    errors.clientLimit = i18n.t("clientLimitMax");
  }

  // START / END validation
  const start = getMinutesFromHHMM(values.startTime);
  const end = getMinutesFromHHMM(values.endTime);

  if (start !== null && end !== null && start >= end) {
    errors.startTime = i18n.t("startMustBeEarlier");
    errors.endTime = i18n.t("endMustBeLater");
  }

  return errors;
};

function ServiceForm(props) {
  const {
    handleSubmit,
    pristine,
    onSubmit,
    actionType,
    resourcesLimited,
    associatedReservationEnable,
    servicesCategoriesOptions,
    projectId,
    mainUserUid,
    existingImageUid,
    formImageUid,
    change,
    actNewAlertMessage,
    actUploadProjectImage,
    actDeleteProjectImage,
  } = props;

  const { t } = useTranslation();
  const weekDaysOptions = getWeekDaysOptions();

  const [imageData, setImageData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isImageMarkedForDelete, setIsImageMarkedForDelete] = useState(false);

  const buildCleanedData = (data, imageUidFinal) => ({
    ...data,
    cost: Number(data?.cost),
    resourcesLimitNumber: Number(data?.resourcesLimitNumber),
    clientLimit: Number(data?.clientLimit),
    startTime: String(data?.startTime),
    endTime: String(data?.endTime),
    serviceDuration: Number(data?.serviceDuration),
    minPreviousTime: Number(data?.minPreviousTime),
    minCancelTime: Number(data?.minCancelTime),
    availableDays: (data?.availableDays || []).map(Number),
    resourcesLimited: Boolean(data?.resourcesLimited),
    autoConfirmateEnable: Boolean(data?.autoConfirmateEnable),
    associatedReservationEnable: Boolean(data?.associatedReservationEnable),
    onlyAssociated: Boolean(data?.onlyAssociated),
    onlyStartInEvents: Boolean(data?.onlyStartInEvents),
    serviceCategories: (data?.serviceCategories || []).map(String),
    imageUid: imageUidFinal || null,
  });

  const handleSave = (data) => {
    if (!projectId) {
      actNewAlertMessage({ type: "warning", title: "image", content: "missingProjectId" });
      return;
    }

    setIsSaving(true);

    if (!imageData) {
      if (isImageMarkedForDelete && existingImageUid) {
        actDeleteProjectImage({
          imageUid: existingImageUid,
          projectId,
          cb: (deletedImageUid) => {
            if (deletedImageUid) {
              onSubmit(buildCleanedData(data, null));
              setIsImageMarkedForDelete(false);
            } else {
              actNewAlertMessage({
                type: "error",
                title: "image",
                content: `${t("Delete")} ${t("unknownError")}`,
              });
            }
            setIsSaving(false);
          },
        });
        return;
      }

      const uidToPersist = data?.imageUid || existingImageUid || null;

      onSubmit(buildCleanedData(data, uidToPersist));
      setIsSaving(false);
      return;
    }

    actUploadProjectImage({
      image: imageData,
      projectId,
      cb: (imageUid) => {
        if (imageUid) {
          change("imageUid", imageUid);

          setImageData(null);

          actNewAlertMessage({
            type: "success",
            title: "image",
            content: `${t("uploadFile")} ${t("Success")}`,
          });

          onSubmit(buildCleanedData(data, imageUid));
          setIsSaving(false);
        } else {
          actNewAlertMessage({
            type: "error",
            title: "image",
            content: `${t("uploadFile")} ${t("unknownError")}`,
          });
          setIsSaving(false);
        }
      },
    });
  };

  const canSave =
    !isSaving &&
    (!pristine ||
      Boolean(isImageMarkedForDelete) ||
      Boolean(imageData) ||
      (Boolean(formImageUid) && formImageUid !== existingImageUid));

  return (
    <MDBox py={3}>
      <form onSubmit={handleSubmit(handleSave)}>
        <Grid container justifyContent="center">
          <Card id="basic-info">
            <MDBox
              height="4rem"
              bgColor="info"
              variant="gradient"
              coloredShadow="info"
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={-3}
              mx={10}
              px={2}
            >
              {t(actionType)} {t("service")}
            </MDBox>

            <MDBox pt={3} pb={3} px={4}>
              <Grid container spacing={4} justifyContent="center">
                <Field name="imageUid" component="input" type="hidden" />

                {/* NAME */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Field name="name" component={FormField} label={t("name")} />
                </Grid>

                {/* SERVICE CATEGORIES */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Field
                    name="serviceCategories"
                    component={FormSelectorMulti}
                    values={servicesCategoriesOptions.values}
                    names={servicesCategoriesOptions.names}
                    label={t("servicesCategories")}
                  />
                </Grid>

                {/* START / END */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 4 }}>
                      <Field name="startTime" component={FormTimeSelector} label={t("startTime")} />
                    </Grid>

                    <Grid size={{ xs: 4 }}>
                      <Field name="endTime" component={FormTimeSelector} label={t("endTime")} />
                    </Grid>
                  </Grid>
                </Grid>

                {/* AVAILABLE DAYS */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Field
                    name="availableDays"
                    component={FormSelectorMulti}
                    values={weekDaysOptions.map((d) => d.uid)}
                    names={weekDaysOptions}
                    label={t("availableDays")}
                  />
                </Grid>

                {/* COST + CURRENCY */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 2 }}>
                      <Field
                        sx={{ maxWidth: "50px" }}
                        name="cost"
                        component={FormField}
                        label={t("cost")}
                      />
                    </Grid>

                    <Grid size={{ xs: 2 }}>
                      <Field
                        sx={{ maxWidth: "80px" }}
                        name="currency"
                        component={FormSelector}
                        values={currencyOptions.values}
                        names={currencyOptions.names}
                        label={t("currency")}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* SERVICE DURATION */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Field
                    name="serviceDuration"
                    component={FormTimeSelector}
                    label={t("serviceDuration")}
                    inMinutes
                  />
                </Grid>

                {/* MIN PREVIOUS + MIN CANCEL */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Field
                        name="minPreviousTime"
                        component={FormTimeSelector}
                        enableDays
                        inMinutes
                        label={t("minPreviousTime")}
                      />
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                      <Field
                        name="minCancelTime"
                        component={FormTimeSelector}
                        enableDays
                        inMinutes
                        label={t("minCancelTime")}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* RESOURCES LIMITED */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Field
                        name="resourcesLimited"
                        component={FormCheckbox}
                        label={t("resourcesLimited")}
                      />
                    </Grid>

                    {resourcesLimited && (
                      <Grid size={{ xs: 3 }}>
                        <Field
                          sx={{ maxWidth: "150px" }}
                          name="resourcesLimitNumber"
                          component={FormField}
                          label={t("resourcesLimitNumber")}
                        />
                      </Grid>
                    )}

                    <Grid size={{ xs: 12, md: 8 }}>
                      <Grid size={{ xs: 3 }}>
                        <Field
                          sx={{ maxWidth: "150px" }}
                          name="clientLimit"
                          component={FormField}
                          label={t("clientLimit")}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                {/* AUTO CONFIRM + ONLY START IN EVENTS + ASSOCIATED */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Field
                        name="autoConfirmateEnable"
                        component={FormCheckbox}
                        label={t("autoConfirmateEnable")}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Field
                        name="onlyStartInEvents"
                        component={FormCheckbox}
                        label={t("onlyStartInEvents")}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Field
                        name="associatedReservationEnable"
                        component={FormCheckbox}
                        label={t("associatedReservationEnable")}
                      />
                    </Grid>

                    {associatedReservationEnable && (
                      <Grid size={{ xs: 6 }}>
                        <Field
                          name="onlyAssociated"
                          component={FormCheckbox}
                          label={t("onlyAssociated")}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>

                {/* ICON NAME */}
                {/* <Grid size={{ xs: 12, md: 8 }}>
                  <Field name="iconName" component={FormField} label={t("iconName")} />
                </Grid> */}

                {/* INFO */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Field name="info" component={FormField} label={t("additionalInfo")} />
                </Grid>

                {/* FOTO */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <UploadImageInput
                    label={t("image")}
                    helpText={`${t("onlyPng")} · ${t("max")} 1MB`}
                    inputId="service-img-file-input"
                    selectLabel={t("Select")}
                    deleteLabel={t("Delete")}
                    uploadPlaceholder={t("uploadFile")}
                    maxBytes={1024 * 1024}
                    projectId={projectId}
                    mainUserUid={mainUserUid}
                    existingImageUid={existingImageUid}
                    formImageUid={formImageUid}
                    onNewAlertMessage={actNewAlertMessage}
                    onSetImageData={(dataUrl) => {
                      setImageData(dataUrl);
                      if (dataUrl) setIsImageMarkedForDelete(false);
                    }}
                    onDeleteImage={(isDeleted) => setIsImageMarkedForDelete(isDeleted)}
                    onChangeImageUid={(uid) => change("imageUid", uid)}
                  />
                </Grid>

                {/* SUBMIT */}
                <Grid size={{ xs: 12 }}>
                  <MDBox display="flex" justifyContent="center" mt={2}>
                    <MDButton
                      type="submit"
                      variant="gradient"
                      color="dark"
                      size="small"
                      disabled={!canSave}
                    >
                      {isSaving ? t("Saving") : t("Save")}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>
      </form>
    </MDBox>
  );
}

ServiceForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  actionType: PropTypes.string.isRequired,
  resourcesLimited: PropTypes.bool,
  associatedReservationEnable: PropTypes.bool,
  servicesCategoriesOptions: PropTypes.shape({
    values: PropTypes.arrayOf(PropTypes.string),
    names: PropTypes.arrayOf(PropTypes.shape({ uid: PropTypes.string, name: PropTypes.string })),
  }),
  projectId: PropTypes.string,
  mainUserUid: PropTypes.string,
  existingImageUid: PropTypes.string,
  formImageUid: PropTypes.string,
  change: PropTypes.func.isRequired,
  actNewAlertMessage: PropTypes.func.isRequired,
  actUploadProjectImage: PropTypes.func.isRequired,
  actDeleteProjectImage: PropTypes.func.isRequired,
};

ServiceForm.defaultProps = {
  resourcesLimited: false,
  associatedReservationEnable: false,
  servicesCategoriesOptions: { values: [], names: [] },
  projectId: "",
  mainUserUid: "",
  existingImageUid: "",
  formImageUid: "",
};

const selector = formValueSelector("ServiceForm");

const mapStateToProps = (state, props) => {
  const { actionType, uids, projectId } = props;

  const services = state.servicesReducer?.services || {};
  const servicesCategoriesDict = state.servicesReducer?.servicesCategories || {};

  const sortedServiceCategories = Object.values(servicesCategoriesDict || {})
    .map((c) => ({
      uid: String(c.uid),
      name: c.name || "",
    }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

  const servicesCategoriesOptions = {
    values: sortedServiceCategories.map((c) => c.uid),
    names: sortedServiceCategories,
  };

  let initial = {
    name: "",
    currency: "EUR",
    cost: 0,
    serviceDuration: 60,
    minPreviousTime: 60,
    minCancelTime: 60,
    resourcesLimited: false,
    resourcesLimitNumber: 1,
    clientLimit: 1,
    autoConfirmateEnable: false,
    associatedReservationEnable: false,
    onlyAssociated: false,
    onlyStartInEvents: false,
    availableDays: [0, 1, 2, 3, 4, 5, 6],
    // iconName: "",
    info: "",
    serviceCategories: [],
    imageUid: null,
  };

  if (actionType === "edit" && uids.length === 1 && services[uids[0]]) {
    initial = { ...initial, ...services[uids[0]] };
    initial.serviceCategories = (initial.serviceCategories || []).map(String);
  }

  return {
    initialValues: initial,
    resourcesLimited: selector(state, "resourcesLimited"),
    associatedReservationEnable: selector(state, "associatedReservationEnable"),
    servicesCategoriesOptions,
    projectId,
    mainUserUid: state?.mainUserReducer?.uid || "",
    existingImageUid: initial?.imageUid || "",
    formImageUid: selector(state, "imageUid") || "",
  };
};

const mapDispatchToProps = (dispatch) => ({
  actNewAlertMessage: (message) => dispatch(newAlertMessage(message)),
  actUploadProjectImage: (data) => dispatch(uploadProjectImage(data)),
  actDeleteProjectImage: (data) => dispatch(deleteProjectImage(data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(reduxForm({ form: "ServiceForm", validate })(ServiceForm));
